import { PAYLOAD_URL } from "~/constants/all";
import { logError } from "~/lib/logger";
import type { ApiResponse, CaughtError, ErrorResponse } from "~/types";

import { restPublicPdf } from "./rest";

/**
 * Interface for form submission preview response
 */

const handleError = (
  error: CaughtError,
  message: string,
  location: string
): ErrorResponse => {
  if (error.message) {
    logError("payloadApiForms", message, {
      error: error.message,
      function: location,
    });

    // Handle other errors with a message property
    return {
      success: false,
      message: error.message,
    };
  } else {
    // Handle unknown errors
    logError("payloadApiForms", message, {
      error: "An unknown error occurred",
      function: location,
    });

    return {
      success: false,
      message: "An unknown error occurred",
    };
  }
};

interface SubmissionData {
  template: string;
  submission: {
    [key: string]: any;
  };
}

export const createFormSubmissionPreview = async ({
  template,
  submission,
}: SubmissionData): Promise<ApiResponse<string>> => {
  if (!template) {
    logError("payloadApiForms", "Creating public form submission preview", {
      action: "form-submission-preview",
      data: submission,
      message: "Template is required",
      function: "createFormSubmissionPreview",
    });

    return {
      success: false,
      message: "Template is required",
    };
  }

  if (!submission) {
    logError("payloadApiForms", "Creating public form submission preview", {
      action: "form-submission-preview",
      data: submission,
      message: "Submission is required",
      function: "createFormSubmissionPreview",
    });

    return {
      success: false,
      message: "Submission is required",
    };
  }

  try {
    logError("payloadApiForms", "Creating public form submission preview", {
      action: "form-submission-preview",
      data: submission,
      function: "createFormSubmissionPreview",
    });

    const res = await restPublicPdf(
      `${PAYLOAD_URL}/api/form-submissions/preview`,
      {
        template,
        submission,
      },
      {
        method: "POST",
      }
    );

    // res is a base64 string, not an object, so no need to check for errors property
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    return handleError(
      error as CaughtError,
      "Creating form submission preview failed",
      "createFormSubmissionPreview"
    );
  }
};
